
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, MessageSquare, Trophy, Target, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ProfileBio from "./dashboard/ProfileBio";
import SubscriptionStatus from "@/components/subscription/SubscriptionStatus";

interface ProfileDashboardProps {
  profile: any;
}

const ProfileDashboard = ({ profile }: ProfileDashboardProps) => {
  const navigate = useNavigate();

  if (!profile) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const quickActions = [
    {
      label: "Find Clubs",
      icon: Users,
      onClick: () => navigate('/clubs'),
      color: "hover:bg-blue-50 hover:border-blue-200"
    },
    {
      label: "Connect",
      icon: MessageSquare,
      onClick: () => navigate('/messages'),
      color: "hover:bg-green-50 hover:border-green-200"
    },
    {
      label: "Opportunities",
      icon: Trophy,
      onClick: () => navigate('/opportunities'),
      color: "hover:bg-orange-50 hover:border-orange-200"
    },
    {
      label: "Explore",
      icon: Star,
      onClick: () => navigate('/explore'),
      color: "hover:bg-purple-50 hover:border-purple-200"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Profile Bio Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ProfileBio 
          bio={profile.bio}
          interests={profile.interests}
          location={profile.location}
          school={profile.school}
          gradeLevel={profile.grade_level}
        />
      </motion.div>

      {/* Subscription Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SubscriptionStatus />
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="hover:shadow-lg transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Discover opportunities and connect with the community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={`justify-start w-full transition-all duration-300 ${action.color}`}
                    onClick={action.onClick}
                  >
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.label}
                  </Button>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ProfileDashboard;
